import { useCallback, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  cn,
} from '@wundo/ui';
import type { RoleplayDebrief } from '@wundo/shared/roleplay.ts';
import {
  PracticeSessionExperience,
  type PracticeSessionPhase,
} from './PracticeSessionExperience.tsx';
import { PracticeReport } from './PracticeReport.tsx';
import { PracticeSessionFeedback } from './PracticeSessionFeedback.tsx';
import { practiceFailureMessage } from './practiceFailureMessage.ts';
import { practiceFeedbackTitle } from './practiceFeedbackTitle.ts';
import { useRoleplaySession } from '@/lib/useRoleplaySession.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeSessionModalProps {
  sessionId: string;
  /** Closes the modal. Must be stable across renders. */
  onLeave: () => void;
  /** Whether a new attempt is being started. */
  starting: boolean;
  /** Whether starting a new attempt failed, said here because the page behind is covered. */
  startFailed: boolean;
  /** Starts a new attempt at this roleplay, offered when the user did not take part. */
  onPractiseAgain: (roleplayId: string) => void;
}

/**
 * A practice conversation held over Oefenen, so leaving it lands on the page as
 * it was. Escape and the backdrop are refused while it runs — the way out is the
 * back link before the start and Beëindig after it. Once the feedback is on
 * screen, the cross closes it like any other dialog.
 */
export function PracticeSessionModal({
  sessionId,
  onLeave,
  starting,
  startFailed,
  onPractiseAgain,
}: PracticeSessionModalProps) {
  const { session, isPending, failure } = useRoleplaySession(sessionId);
  /* Null on a session from before roleplays were rows: there is nothing to start again. */
  const roleplayId = session?.roleplay_id ?? null;

  /*
   * The feedback this conversation produced, once there is any. Undefined while
   * the conversation is still running; null when nobody spoke and there is none
   * to write.
   */
  const [debrief, setDebrief] = useState<RoleplayDebrief | null | undefined>(undefined);
  const onDebrief = useCallback((next: RoleplayDebrief | null) => setDebrief(next), []);

  /*
   * The modal follows the conversation's size. The brief and the debrief are
   * read at their own height, like any dialog; a live conversation takes all
   * the height there is, because a chat column or a voice stage in a
   * content-sized box would be a letterbox.
   */
  const [phase, setPhase] = useState<PracticeSessionPhase>('brief');
  const onPhaseChange = useCallback((next: PracticeSessionPhase) => setPhase(next), []);

  /* The conversation is over the moment there is feedback to read. */
  const reading = debrief !== undefined;

  /*
   * A running conversation takes the whole window, and so does a report: its
   * feedback and transcript scroll inside it, which needs a height to scroll
   * in. The phase it was left on says nothing once the feedback is in.
   */
  const fullHeight = reading ? debrief !== null : phase === 'live';

  return (
    <Dialog
      open
      disablePointerDismissal
      onOpenChange={(next, details) => {
        if (next) {
          return;
        }

        if (reading) {
          onLeave();
          return;
        }

        details.cancel();
      }}
    >
      <DialogContent
        size='full'
        aria-label={m.practice_session_title()}
        className={cn(
          /*
           * The width of what is in it, in every phase. The conversation and
           * the brief are the same 960px column plus its own padding, live or
           * not — a popup wider than that is two empty gutters with a column
           * adrift in the middle of them. The report is narrower still,
           * because it is read at the width every other report in the app is.
           */
          'max-w-250',
          !fullHeight && 'h-auto max-h-[calc(100dvh-6rem)]',
          /* The report scrolls inside itself; the dialog never does as well. */
          reading && 'max-w-190 overflow-hidden rounded-lg',
        )}
      >
        {isPending && (
          <PracticeSessionModalNotice
            message={m.practice_session_loading()}
            onClose={onLeave}
          />
        )}
        {failure !== null && (
          <PracticeSessionModalNotice
            message={practiceFailureMessage(failure)}
            onClose={onLeave}
          />
        )}
        {session !== undefined && !reading && (
          <PracticeSessionExperience
            key={session.id}
            session={session}
            onLeave={onLeave}
            onDebrief={onDebrief}
            onPhaseChange={onPhaseChange}
          />
        )}
        {reading && (
          <>
            <DialogHeader className='gap-4 border-line px-6.5 py-5.5'>
              <DialogTitle className='text-xl font-medium tracking-tight'>
                {practiceFeedbackTitle(debrief)}
              </DialogTitle>
              <DialogClose
                aria-label={m.practice_close()}
                className='size-9 rounded-md bg-fill hover:bg-line'
              />
            </DialogHeader>

            {debrief !== null && session !== undefined ? (
              <PracticeReport
                sessionId={session.id}
                personaName={session.scenario.persona.name}
                debrief={debrief}
                className='pb-6.5'
                fill
                notTakenPartActions={
                  <>
                    {roleplayId !== null && (
                      <Button
                        disabled={starting}
                        onClick={() => onPractiseAgain(roleplayId)}
                      >
                        <RotateCcw className='size-3.5' aria-hidden='true' />
                        {m.practice_start_again()}
                      </Button>
                    )}
                    <Button
                      variant='quiet'
                      shape='block'
                      className='px-4 font-medium'
                      onClick={onLeave}
                    >
                      {m.practice_close()}
                    </Button>
                    {startFailed && (
                      <p role='alert' className='w-full text-base text-danger'>
                        {m.practice_start_failed()}
                      </p>
                    )}
                  </>
                }
              />
            ) : (
              <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6.5 pt-5.5 pb-6.5'>
                <PracticeSessionFeedback debrief={debrief} />
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface PracticeSessionModalNoticeProps {
  message: string;
  onClose: () => void;
}

/**
 * Anything that is not a conversation yet, or never will be.
 *
 * Unlike the dedicated address, this one always offers the way out: whoever is
 * reading it is signed in and standing on Oefenen, and the modal is the only
 * thing between them and it.
 */
function PracticeSessionModalNotice({
  message,
  onClose,
}: PracticeSessionModalNoticeProps) {
  return (
    <div className='flex flex-1 flex-col items-center justify-center gap-4 px-5 py-20 text-center'>
      <p role='status' className='max-w-120 text-base text-ink-body'>
        {message}
      </p>
      <Button variant='quiet' onClick={onClose}>
        {m.practice_close()}
      </Button>
    </div>
  );
}
